<template>
	<v-list class="resource-list pa-0 pt-5">
		<div
			v-for="resourceItem in resourcesItems"
			:key="resourceItem.resource_id"
			class="resource-list__item"
		>
			<v-list-item-content class="d-flex pa-0">
				<!-- folder	-->
				<v-card-title class="resource-link pa-3" v-if="resourceItem.resource_type === 3">
					<SponsorBadge
						v-if="resourceItem.sponsored_by || resourceItem.pin_to_top"
						:text="sponsorText(resourceItem)"
						:top="'0px'"
						:right="'0px'"
						:iconSize="10"
						:iconColor="'#ffffff'"
						:icon="resourceItem.sponsored_by ? 'star' : 'pinned'"
					/>
					<router-link
						:to="folderLink(resourceItem)"
						exact
						class="text-decoration-none d-flex"
						style="width: 100%"
					>
						<Icon
							:type="resourceItem.ico"
							:color="resourceItem.color"
							:size="40"
							:pr="12"
						/>
						<v-list-item-title
							class="resource-link__folder-name mb-0"
							style="text-decoration: underline"
						>
							{{ resourceItem.resources_name }}
						</v-list-item-title>
					</router-link>
				</v-card-title>

				<span>{{ resourceItem.count }}</span>

				<!-- file	-->
				<div
					class="resource-link pa-3"
					v-if="resourceItem.resource_type === 1 || resourceItem.resource_type === 2"
				>
					<AdvancedResourceLink
						:link="fileLink(resourceItem)"
						:isAdvanced="isAdvancedResources"
					>
						<div
							v-if="resourceItem.resource_image && isAdvancedResources"
							class="resource-list__photo-wrapper"
						>
							<img
								:src="resourceItem.resource_image"
								class="resource-list__photo-wrapper__img mr-4"
								alt="Resource image"
							/>
							<div class="resource-list__photo-wrapper__icon mr-0">
								<ResourceFileIcon
									width="12"
									height="12"
									:fileName="resourceItem.upload.file_real_name"
									:url="resourceItem.upload.url"
								/>
							</div>
						</div>
						<ResourceFileIcon
							v-else
							:fileName="resourceItem.upload.file_real_name"
							:url="resourceItem.upload.url"
							:mr="12"
						/>

						<div class="resource-link__text-wrapper">
							<p
								class="resource-link__file-name mb-0"
								style="text-decoration: underline"
							>
								{{
									resourceItem.resources_name ||
									resourceItem.upload.file_real_name
								}}
							</p>
							<span
								v-if="!isAdvancedResources"
								class="resource-link__file-release-date"
							>
								{{ dateCreated(resourceItem.upload.time) }}
							</span>
						</div>
					</AdvancedResourceLink>
					<div
						class="d-flex align-center mt-2"
						:style="{
							justifyContent: resourceAuthor(resourceItem)
								? 'space-between'
								: 'flex-end ',
						}"
						v-if="isAdvancedResources"
					>
						<span
							v-if="resourceAuthor(resourceItem)"
							class="resource-link__file-author"
						>
							{{ resourceAuthor(resourceItem) }}
						</span>
						<span
							v-if="resourceReleaseDate(resourceItem)"
							class="resource-link__file-date"
						>
							{{ resourceReleaseDate(resourceItem) }}
						</span>
					</div>
				</div>
			</v-list-item-content>
		</div>
	</v-list>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import AdvancedResourceLink from '@/components/Resource/AdvancedResourceLink.vue'
import ResourceFileIcon from '@/components/Resource/ResourceFileIcon.vue'
import Icon from '@/components/Icon'
import SponsorBadge from '@/components/SponsorBadge.vue'

export default {
	name: 'ResourceList',
	components: {
		SponsorBadge,
		Icon,
		AdvancedResourceLink,
		ResourceFileIcon,
	},
	data() {
		return {
			fileColors: [
				{ color: '#50bee8', ext: ['jpg', 'jpeg', 'psd'] },
				{ color: '#6678aa', ext: ['bmp'] },
				{ color: '#a066aa', ext: ['png', 'gif'] },
				{ color: '#f15642', ext: ['pdf'] },
				{ color: '#50bee8', ext: ['doc', 'docx'] },
				{ color: '#638674', ext: ['rtf'] },
				{ color: '#f15642', ext: ['ppt'] },
				{ color: '#84bd5a', ext: ['xls', 'xlsx'] },
				{ color: '#f7b84e', ext: ['mp3', 'wav', 'flac'] },
				{ color: '#f7b84e', ext: ['avi', 'mp4', 'mpeg'] },
				{ color: '#576d7e', ext: ['txt'] },
				{ color: '#e59f2b', ext: ['zip', 'rar', '7z', 'gz', 'gzip', 'bzip'] },
				{ color: '#41a8e1', ext: ['url'] },
			],
		}
	},
	props: {
		resourcesItems: {
			type: Array,
			default: () => [],
		},
	},
	computed: {
		...mapGetters('resources', ['resourcesList', 'isLoading', 'listMeta', 'breadCrumbs']),
		isAdvancedResources() {
			return parseInt(this.$route.query.advance, 10) === 1
		},
		pinned() {
			return 'pinned'
		},
	},
	methods: {
		...mapActions('resources', ['breadCrumbHandler']),
		sponsorText(item) {
			if (item && item.sponsored_by) return `Sponsored by ${item.sponsored_by}`
			if (item && item.pin_to_top) return 'Pinned'
			return ''
		},

		formateBreadCrumbs(resourceId) {
			const listIds = this.breadCrumbs.map((item) => item.id)

			if (_.isEmpty(listIds)) {
				return {}
			}

			return {
				bread_crumbs: [...listIds, +resourceId].join(','),
			}
		},
		fileIconColor(file) {
			if (!file.filename) return '#8b8b8b'

			const extName = this.fileIconName(file)
			const colorDataObj = extName && this.fileColors.find((i) => i.ext.indexOf(extName) > -1)

			return colorDataObj ? colorDataObj.color : '#8b8b8b'
		},
		dateCreated(originalDate) {
			const date = new Date(originalDate * 1000)
			const options = { year: 'numeric', month: 'long', day: 'numeric' }
			return date.toLocaleDateString('en-US', options)
		},
		fileIconName(file) {
			if (file.metadata) {
				return (file.filename || '').split('.').pop().substring(0, 4)
			}

			return 'url'
		},
		backgroundImage(resourceItem) {
			return resourceItem.resource_image ||
				(resourceItem.upload.metadata &&
					resourceItem.upload.metadata.mime.includes('image'))
				? resourceItem.resource_image || resourceItem.upload.url
				: null
		},
		resourceAuthor(resourceItem) {
			return resourceItem.resource_author || ''
		},
		resourceReleaseDate(resourceItem) {
			if (resourceItem.resource_release_date) {
				const date = new Date(resourceItem.resource_release_date * 1000)
				const options = {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				}
				const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date)
				return formattedDate
			} else if (resourceItem.upload && resourceItem.upload.time) {
				return this.dateCreated(resourceItem.upload.time)
			}
			return ''
		},
		fileType(resourceItem) {
			if (resourceItem.upload.metadata) {
				return resourceItem.upload.filename.substring(
					resourceItem.upload.filename.lastIndexOf('.') + 1
				)
			} else {
				return 'file'
			}
		},
		folderLink(resourceItem) {
			const params = {
				name: 'resources',
				query: {
					category_id: resourceItem.resource_id,
					advance: this.isAdvancedResources ? 1 : 0,
					title: resourceItem.resources_name,
				},
			}
			return params
		},
		fileLink(resourceItem) {
			return this.isAdvancedResources
				? {
						name: 'resource-file',
						query: {
							category_id: resourceItem.resource_id,
							advance: this.isAdvancedResources ? 1 : 0,
						},
				  }
				: resourceItem.upload.url
		},
	},
	created() {
		//
	},
}
</script>

<style lang="scss">
.resource-list {
	background: transparent !important;
	&__photo-wrapper {
		height: 44px;
		position: relative;
		&__img {
			height: 44px;
			width: 44px;
			border-radius: 8px;
		}
		&__icon {
			position: absolute;
			bottom: 4px;
			right: 9px;
		}
	}
}

.resource-link {
	background-color: #fff;
	border-radius: 12px;
	margin-bottom: 8px !important;
	border: 1px solid #e2e2e2;
	max-width: 100%;
	position: relative;

	&__image {
		width: 44px;
		height: 44px;
		margin-right: 8px;
		border-radius: 8px;

		&-icon {
			img {
				height: 12px;
				width: 12px;
				position: absolute;
				bottom: 4px;
				right: 2px;
			}
		}
	}

	&__file,
	&__folder {
		&-name {
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 14px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--ceil-detail-text-color);
			text-decoration: unset !important;
		}

		&-release-date {
			font-size: 13px;
			font-weight: 500;
			color: #a0a0a0;
			line-height: 16px;
		}

		&-author {
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
		}
		&-date {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: #9b9b9b;
		}
	}

	&__file {
		&-name {
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
			word-break: break-word;
		}
	}

	&__text-wrapper {
		max-width: calc(100% - 56px);
	}
}
</style>
